/**
 * Plot Workshop writer-facing copy (#1080 / #995 / #1180).
 * Generate stays closed until Ready. A soft warning does not start a spend.
 */

import {
  MAKE_A_NOTE,
  THIS_BECOMES_THIS_BOOKS_FRAMEWORK,
  THIS_IS_THIS_BOOKS_FRAMEWORK,
} from './writerCopy';
import {
  NOTES_HANDY_COPY,
  NO_FRAMEWORK_STEER_COPY,
  THIN_WARN_COPY,
  OUTLINE_EXISTS_NEXT_STEP,
  WORKSHOP_GENERATE_BLOCKED,
} from './plottrackGenerate';

export const PLOT_READY_NAME = 'Plot Workshop';

export const PLOT_WORKSHOP_NAME = PLOT_READY_NAME;

export const PLOT_READY_TIP =
  'Work your story one sequence at a time. Each sequence you approve goes on the Plot Grid.';

export const PLOT_READY_OPEN_BOOK = 'Open a book to look at the plot.';

export const PLOT_READY_OPEN_BOOK_TITLE = 'Open a book on Plot Workshop';

export const PLOT_READY_BOARD_LABEL = 'What’s still needed';

export const WORKSHOP_STATUS_NOT_READY = 'Not ready yet';

export const WORKSHOP_STATUS_READY = 'Ready to generate the outline';

export const WORKSHOP_READY_NEXT =
  'Next: edit the outline on Plot Grid, then write scenes.';

export const WORKSHOP_TALK_HERE = 'This book’s chat is here.';

// Retired from Plot Workshop Ready (#1157). Kept so older imports do not break.
// Do not paint these on the writer-facing board.
export const WORKSHOP_HAVE_LABEL = 'What we have';

export const WORKSHOP_HAVE_SHOW = 'Show what we have';

export const WORKSHOP_HAVE_HIDE = 'Hide what we have';

export const WORKSHOP_READY_HONEST =
  'Ready means there is enough to generate. It is not a promise the outline will match the book you want.';

export const WORKSHOP_OPTIONAL_BEATS_LABEL = 'Sharpen a Framework sequence (optional)';

export const WORKSHOP_OPTIONAL_BEATS_SHOW = 'Show Framework sequences (optional)';

export const WORKSHOP_OPTIONAL_BEATS_HIDE = 'Hide Framework sequences';

export const WORKSHOP_OPTIONAL_BEATS_READY_EMPTY =
  'Framework sequences are optional. Generate opens once this book has its main character, what they want, and what\'s in the way.';

export const READINESS_READY = 'Ready';

export const READINESS_NOT_READY = 'Not ready';

export const PLOT_WORKSHOP_BEATS_LABEL = 'Framework sequences';

export const PLOT_WORKSHOP_EMPTY_FRAMEWORK =
  'No Framework yet. Choose a Framework, then work it one sequence at a time.';

export const PLOT_WORKSHOP_CHOOSE_FRAMEWORK = 'Choose Framework';

export const PLOT_WORKSHOP_CHANGE_FRAMEWORK = 'Change Framework';

export { THIS_IS_THIS_BOOKS_FRAMEWORK };

export const PLOT_WORKSHOP_WORK_BEAT = 'Work this sequence';

export const PLOT_WORKSHOP_PURPOSE_SHOW = 'Show purpose';

export const PLOT_WORKSHOP_PURPOSE_HIDE = 'Hide purpose';

export const PLOT_WORKSHOP_FILL_SHOW = 'Show this sequence’s fill';

export const PLOT_WORKSHOP_FILL_HIDE = 'Hide this sequence’s fill';

export const PLOT_WORKSHOP_FILL_EDIT = 'Edit description';

export const PLOT_WORKSHOP_FILL_SAVE = 'Save description';

export const PLOT_WORKSHOP_FILL_CANCEL = 'Cancel';

export const PLOT_WORKSHOP_FILL_SAVING = 'Saving…';

export const PLOT_WORKSHOP_FILL_EDIT_FIELD = 'Sequence description';

export const PLOT_WORKSHOP_FILL_SAVE_ERROR = 'Could not save this sequence.';

export function editBeatDescriptionLabel(beatName) {
  const name = String(beatName || '').trim();
  return name ? `Edit description: ${name}` : PLOT_WORKSHOP_FILL_EDIT;
}

export const PLOT_WORKSHOP_THIN = 'Thin';

export const PLOT_WORKSHOP_MISSING = 'Missing';

export const PLOT_WORKSHOP_ENOUGH =
  'Enough context to generate a Plot Grid outline. You can still fill more, or build the outline by hand.';

export const SOLID_FOLD_LABEL = 'Already solid';

export const SOLID_FOLD_SHOW = 'Show what’s already solid';

export const SOLID_FOLD_HIDE = 'Hide what’s already solid';

export const PRIMARY_NEXT_LABEL = 'Plot Grid outline';

export const PRIMARY_NEXT_TIP =
  'When you have enough, generate the outline on Plot Grid. You can also build the outline by hand.';

export function workshopIsReady(workshop) {
  if (!workshop) return false;
  if (typeof workshop.ready_to_generate === 'boolean') return workshop.ready_to_generate;
  return Boolean(workshop.enough_solid);
}

export const GENERATE_OUTLINE_ON_PLOTTRACK = 'Open Plot Grid to generate';

export const OPEN_OUTLINE_ON_PLOTTRACK = 'Review Plot Grid';

export const OPEN_PLOTTRACK = OPEN_OUTLINE_ON_PLOTTRACK;

export const THIN_OUTLINE_WARNING = THIN_WARN_COPY;

export { OUTLINE_EXISTS_NEXT_STEP, WORKSHOP_GENERATE_BLOCKED };

export const FRAMEWORK_STEER_COPY = NO_FRAMEWORK_STEER_COPY;

export const NOTES_HANDY_STEER = NOTES_HANDY_COPY;

export const STILL_OPEN_PREFIX = 'Still to do: ';

export const STILL_OPEN_FRAMEWORK = 'Framework sequences';

export const PLOT_READY_COL_HAVE = 'Have';
export const PLOT_READY_COL_MISSING = 'Missing / thin';
export const PLOT_READY_COL_DO_THIS = 'Do this';
export const PLOT_READY_COL_WHERE = 'Where';

export const PLOT_READY_HAVE_EMPTY = 'Not yet';

export const PLOT_READY_WHERE_LABELS = {
  discovery: 'Discovery',
  character_focus: 'Character Focus',
  plot_focus: 'Plot Focus',
  world_focus: 'World Focus',
  make_card: MAKE_A_NOTE,
  framework: 'Framework',
};

export const DOSSIER_FRAMEWORK_HAVE =
  "This is this book's Framework. Generate uses these act names.";

export const DOSSIER_FRAMEWORK_SELECT_HINT = THIS_BECOMES_THIS_BOOKS_FRAMEWORK;

export const DOSSIER_AUDIT_TIP = (
  'Recommended before you generate a Framework. Looks up — act-level state '
  + 'changes and Framework sequences. Gaps become questions for the '
  + "session about the book's argument. "
  + 'Brainstormy will not generate a plot on a missing foundation.'
);

/** Writer-facing Framework strings on Plot Workshop / dossiers. */
export const FRAMEWORK_UI_STRINGS = [
  PLOT_WORKSHOP_BEATS_LABEL,
  PLOT_WORKSHOP_EMPTY_FRAMEWORK,
  PLOT_WORKSHOP_CHOOSE_FRAMEWORK,
  PLOT_WORKSHOP_CHANGE_FRAMEWORK,
  THIS_IS_THIS_BOOKS_FRAMEWORK,
  STILL_OPEN_FRAMEWORK,
  FRAMEWORK_STEER_COPY,
  PLOT_READY_WHERE_LABELS.framework,
  DOSSIER_FRAMEWORK_HAVE,
  DOSSIER_FRAMEWORK_SELECT_HINT,
  DOSSIER_AUDIT_TIP,
  WORKSHOP_OPTIONAL_BEATS_LABEL,
  WORKSHOP_OPTIONAL_BEATS_SHOW,
  WORKSHOP_OPTIONAL_BEATS_HIDE,
  WORKSHOP_OPTIONAL_BEATS_READY_EMPTY,
];

const BANNED_PRODUCT_COPY = /\b(hang|hung|fence|ledger)\b/i;
const STRUCTURE_WORD = /\bstructure\b/i;

export function workThisBeatLabel(beatName) {
  const name = String(beatName || '').trim();
  return name ? `Work this sequence: ${name}` : PLOT_WORKSHOP_WORK_BEAT;
}

export function outlinePrimaryLabel(hasOutline) {
  return hasOutline ? OPEN_OUTLINE_ON_PLOTTRACK : GENERATE_OUTLINE_ON_PLOTTRACK;
}

export function beatHasBlurb(beat) {
  return Boolean(String(beat?.content || '').trim());
}

export function thinBeats(workshop) {
  return (workshop?.beats || []).filter((beat) => Boolean(beat?.thin));
}

/** Thin rows that already have a blurb. Roster lists every beat; Work this beat is on every row. */
export function optionalSharpenBeats(workshop) {
  return thinBeats(workshop).filter(beatHasBlurb);
}

/** All Framework beats for the optional roster. Empty fills still appear. */
export function frameworkRosterBeats(workshop) {
  return (workshop?.beats || []).filter((beat) => String(beat?.name || '').trim());
}

/** One-line note under the Framework roster: what Ready needs. */
export function optionalFoldQuietCopy(workshop) {
  if (workshop?.has_outline) return '';
  if (workshop?.has_framework && !workshop?.empty_framework) {
    return WORKSHOP_OPTIONAL_BEATS_READY_EMPTY;
  }
  if (workshopIsReady(workshop) && optionalSharpenBeats(workshop).length === 0) {
    return WORKSHOP_OPTIONAL_BEATS_READY_EMPTY;
  }
  return WORKSHOP_OPTIONAL_BEATS_LABEL;
}

export function solidBeats(workshop) {
  return (workshop?.beats || []).filter(
    (beat) => !beat?.thin && String(beat?.content || '').trim(),
  );
}

export function workshopThinWarn(workshop) {
  if (workshopIsReady(workshop)) return '';
  const known = String(workshop?.thin_outline_warning || workshop?.thin_write_warning || '').trim();
  if (known) return known;
  if (workshop?.enough_solid === false) return THIN_OUTLINE_WARNING;
  return '';
}

export function workshopFrameworkSteer(workshop) {
  if (!(workshop?.empty_framework || workshop?.has_framework === false)) return '';
  return String(workshop?.empty_framework_copy || PLOT_WORKSHOP_EMPTY_FRAMEWORK).trim();
}

export function assertNoBannedWorkshopCopy(text) {
  if (BANNED_PRODUCT_COPY.test(String(text || ''))) {
    throw new Error('Writer-facing copy uses a banned product name');
  }
  return text;
}

export function assertFrameworkCopySaysFramework(text) {
  const value = String(text || '');
  if (STRUCTURE_WORD.test(value)) {
    throw new Error('Writer-facing Framework copy cannot say structure');
  }
  if (!/Framework/.test(value)) {
    throw new Error('Writer-facing Framework copy must say Framework');
  }
  return assertNoBannedWorkshopCopy(value);
}

function addStillOpenName(names, seen, raw) {
  const name = String(raw || '').trim();
  const key = name.toLowerCase();
  if (!name || seen.has(key)) return;
  seen.add(key);
  names.push(name);
}

/**
 * What still gates Generate (#1322): the server's open steps. Older payloads
 * without steps fall back to starving foundation holes. Not empty beats.
 */
export function stillOpenNames(workshop) {
  const names = [];
  const seen = new Set();
  if (Array.isArray(workshop?.open_steps)) {
    workshop.open_steps.forEach((name) => addStillOpenName(names, seen, name));
    return names;
  }
  const rows = Array.isArray(workshop?.starving) && workshop.starving.length
    ? workshop.starving
    : (workshop?.holes || []).map((name) => ({ name }));
  rows.forEach((row) => addStillOpenName(names, seen, row?.name || row));
  return names;
}

export function slotHaveLine(slot) {
  const have = String(slot?.have || '').trim();
  if (have) return have;
  if (slot?.needed === false) {
    return String(slot?.missing || '').trim() || PLOT_READY_HAVE_EMPTY;
  }
  return PLOT_READY_HAVE_EMPTY;
}

export function stillOpenLine(workshopOrNames) {
  const names = Array.isArray(workshopOrNames)
    ? workshopOrNames.filter(Boolean)
    : stillOpenNames(workshopOrNames);
  if (!names.length) return '';
  return `${STILL_OPEN_PREFIX}${names.join(', ')}`;
}
